import type React from 'react';
import { useMemo } from 'react';

import { useRoleTaxonomyVersions } from '../selection-lists/hooks/useRoleTaxonomyAdapter';
import { BaseTaxonomyIdSelect } from './BaseTaxonomyIdSelect';
import { useCustomTaxonomyAllowlist } from './utils/customTaxonomyAllowlist';
import { getPrettyTaxonomyName } from './utils/roleTaxonomy';

type RoleTaxonomyCommonProps = {
  isDisabled?: boolean;
  includeCustom?: boolean;
  currentResolvedAdaptiveLabel?: string;
  variant?: 'default' | 'compact';
  usePortal?: boolean;
  selectedLabelPrefix?: string;
};

type RoleTaxonomyPropsWithAdaptive = RoleTaxonomyCommonProps & {
  includeAdaptive: true;
  value?: number | 'adaptive';
  onChange: (taxonomyId: number | 'adaptive') => void;
};

type RoleTaxonomyPropsWithoutAdaptive = RoleTaxonomyCommonProps & {
  includeAdaptive?: false | undefined;
  value?: number;
  onChange: (taxonomyId: number) => void;
};

// Accept dynamic boolean flags by widening value/onChange when boolean is used
type RoleTaxonomyPropsWithDynamicFlag = RoleTaxonomyCommonProps & {
  includeAdaptive: boolean;
  value?: number | 'adaptive';
  onChange: (taxonomyId: number | 'adaptive') => void;
};

const ONET = { id: 61, label: 'ONET' };
const ESCO = { id: 62, label: 'ESCO' };
const LIGHTCAST = { id: 63, label: 'Lightcast' };

export const CustomTaxonomies = [ONET, ESCO, LIGHTCAST];

export type RoleTaxonomyIdSelectProps =
  | RoleTaxonomyPropsWithAdaptive
  | RoleTaxonomyPropsWithoutAdaptive
  | RoleTaxonomyPropsWithDynamicFlag;

export const RoleTaxonomyIdSelect: React.FC<RoleTaxonomyIdSelectProps> = (
  props
) => {
  const {
    isDisabled = false,
    currentResolvedAdaptiveLabel,
    includeCustom = false,
    variant = 'default',
    usePortal = false,
    selectedLabelPrefix,
  } = props;
  const { taxonomies } = useRoleTaxonomyVersions();
  const { role: allowedCustomIds } = useCustomTaxonomyAllowlist();

  const customTaxonomies = useMemo(() => {
    const result: Array<{ id: number; label: string }> = [];

    if (taxonomies && taxonomies.length > 0) {
      allowedCustomIds.forEach((id) => {
        const meta = taxonomies.find((t) => t.taxonomyId === id && t.isLatest);
        if (meta) {
          result.push({
            id,
            label: getPrettyTaxonomyName(meta.taxonomyName),
          });
        }
      });
    } else {
      [ONET, ESCO, LIGHTCAST].forEach((t) => {
        if (allowedCustomIds.includes(t.id)) result.push(t);
      });
    }

    return result;
  }, [taxonomies, allowedCustomIds]);

  if (props.includeAdaptive) {
    return (
      <BaseTaxonomyIdSelect
        includeAdaptive
        value={props.value}
        onChange={props.onChange}
        isDisabled={isDisabled}
        currentResolvedAdaptiveLabel={currentResolvedAdaptiveLabel}
        includeCustom={includeCustom}
        variant={variant}
        usePortal={usePortal}
        customTaxonomies={customTaxonomies}
        selectedLabelPrefix={selectedLabelPrefix}
      />
    );
  }

  return (
    <BaseTaxonomyIdSelect
      value={props.value as number | undefined}
      onChange={props.onChange}
      isDisabled={isDisabled}
      currentResolvedAdaptiveLabel={currentResolvedAdaptiveLabel}
      includeCustom={includeCustom}
      variant={variant}
      usePortal={usePortal}
      customTaxonomies={customTaxonomies}
      selectedLabelPrefix={selectedLabelPrefix}
    />
  );
};
